import { useState } from 'react';
import axios from 'axios';
import { 
  Download, FileSpreadsheet, Users, Clock, Award, CreditCard, ClipboardList,
  CheckCircle, Loader2, Database, Upload
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const exports = [
  {
    id: 'stagiaires',
    title: 'Liste des Stagiaires',
    description: 'Nom, prenom, contact, adresse, centre, entreprise, tuteur, date inscription',
    icon: Users,
    color: 'blue',
    endpoint: '/exports/stagiaires',
    usage: 'Indicateur 1 - Information du public'
  },
  {
    id: 'presences',
    title: 'Presences & Assiduite',
    description: 'Feuilles de presence detaillees + resume du taux d\'assiduite par stagiaire',
    icon: Clock,
    color: 'green',
    endpoint: '/exports/presences',
    usage: 'Indicateur 19 - Assiduite des apprenants'
  },
  {
    id: 'competences',
    title: 'Competences & Blocs',
    description: 'Validation des 3 BC, autonomies validees, taux de diplomes',
    icon: Award,
    color: 'amber',
    endpoint: '/exports/competences',
    usage: 'Indicateur 11 - Atteinte des objectifs'
  },
  {
    id: 'financier',
    title: 'Donnees Financieres',
    description: 'Factures, paiements, soldes, echeanciers avec totaux',
    icon: CreditCard,
    color: 'purple',
    endpoint: '/exports/financier',
    usage: 'Suivi financier et facturation'
  },
  {
    id: 'grilles',
    title: 'Grilles d\'Evaluation',
    description: 'Evaluations du tuteur par critere et par periode avec code couleur',
    icon: ClipboardList,
    color: 'orange',
    endpoint: '/exports/grilles-evaluation',
    usage: 'Indicateur 5 - Evaluation des acquis'
  },
];

const colorMap = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500', badge: 'bg-blue-100 text-blue-700' },
  green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-500', badge: 'bg-green-100 text-green-700' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-700' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-500', badge: 'bg-purple-100 text-purple-700' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-500', badge: 'bg-orange-100 text-orange-700' },
};

const ExportsPage = () => {
  const [downloading, setDownloading] = useState({});

  const handleDownload = async (exp) => {
    setDownloading(prev => ({ ...prev, [exp.id]: true }));
    try {
      const response = await axios.get(exp.endpoint, { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `FISL_${exp.id}_${new Date().toISOString().split('T')[0]}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${exp.title} telecharge !`);
    } catch (e) {
      toast.error(`Erreur lors du telechargement: ${e.response?.data?.detail || e.message}`);
    }
    setDownloading(prev => ({ ...prev, [exp.id]: false }));
  };

  const handleDownloadAll = async () => {
    for (const exp of exports) {
      await handleDownload(exp);
    }
  };

  return (
    <div className="space-y-6" data-testid="exports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-display font-bold text-[#0F172A] mb-2">Exports de Donnees</h1>
          <p className="text-slate-600">Generez des fichiers Excel pour vos audits Qualiopi et votre suivi</p>
        </div>
        <Button
          className="bg-[#8DC63F] hover:bg-[#7ab535] text-white"
          onClick={handleDownloadAll}
          data-testid="btn-download-all"
        >
          <Download className="h-4 w-4 mr-2" />
          Tout telecharger
        </Button>
      </div>

      {/* Info Qualiopi */}
      <div className="bg-[#0F172A] text-white rounded-lg p-5 flex items-start gap-4">
        <FileSpreadsheet className="h-8 w-8 text-[#8DC63F] flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="font-semibold text-lg mb-1">Exports pour Audit Qualiopi</h2>
          <p className="text-slate-300 text-sm">
            Ces exports couvrent les principaux indicateurs Qualiopi : information du public (I1),
            evaluation des acquis (I5), atteinte des objectifs (I11), et assiduite (I19).
            Chaque fichier est formate avec des couleurs pour une lecture rapide.
          </p>
        </div>
      </div>

      {/* Export Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {exports.map(exp => {
          const c = colorMap[exp.color];
          const Icon = exp.icon;
          const isLoading = downloading[exp.id];
          return (
            <div
              key={exp.id}
              className={`${c.bg} border ${c.border} rounded-lg p-5 flex flex-col justify-between`}
              data-testid={`export-card-${exp.id}`}
            >
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Icon className={`h-6 w-6 ${c.icon}`} />
                  <h3 className="font-semibold text-[#0F172A]">{exp.title}</h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">{exp.description}</p>
                <Badge className={`${c.badge} text-xs`}>{exp.usage}</Badge>
              </div>
              <Button
                variant="outline"
                className="mt-4 w-full border-slate-300 hover:bg-white"
                onClick={() => handleDownload(exp)}
                disabled={isLoading}
                data-testid={`btn-download-${exp.id}`}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {isLoading ? 'Telechargement...' : 'Telecharger Excel'}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Backup JSON */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#0F172A] flex items-center justify-center flex-shrink-0">
            <Database className="h-6 w-6 text-[#8DC63F]" />
          </div>
          <div>
            <h3 className="font-semibold text-[#0F172A]">Sauvegarde complete (JSON)</h3>
            <p className="text-sm text-slate-500">
              Exporte toutes les collections de la base de donnees en un seul fichier JSON.
              Utilisable pour restaurer via la page Import.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button
            className="bg-[#0F172A] hover:bg-[#1e293b] text-white"
            onClick={async () => {
              setDownloading(prev => ({ ...prev, backup: true }));
              try {
                const res = await axios.get('/backup/export', { responseType: 'blob' });
                const cd = res.headers['content-disposition'] || '';
                const match = cd.match(/filename="?([^";\n]+)"?/);
                const fname = match ? match[1] : `fisl_backup_${new Date().toISOString().split('T')[0]}.json`;
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fname);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Backup JSON telecharge !');
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Erreur lors du telechargement');
              }
              setDownloading(prev => ({ ...prev, backup: false }));
            }}
            disabled={downloading.backup}
            data-testid="btn-download-backup"
          >
            {downloading.backup ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {downloading.backup ? 'Telechargement...' : 'Telecharger JSON'}
          </Button>
          <Link to="/import">
            <Button variant="outline" className="border-slate-300" data-testid="btn-go-import">
              <Upload className="h-4 w-4 mr-2" />Importer
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer info */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center gap-3">
        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
        <p className="text-sm text-slate-600">
          Les fichiers sont generes en temps reel avec les donnees les plus recentes.
          Format Excel (.xlsx) compatible avec Microsoft Excel, Google Sheets et LibreOffice.
        </p>
      </div>
    </div>
  );
};

export default ExportsPage;
